/**
 * classifyElevationEmpty — ELV-4 diagnostic (row 0.99220).
 *
 * Background (Davenport, IA, 2026-05-18 live probe):
 *   The Refresh elevation CTA (ELV-2) fires, [provider:elevation]
 *   request/response pairs land in Cloud Run logs, and the toast surfaces
 *   "ELEVATION FETCH RETURNED NO USABLE SAMPLES." On a 22-point boundary
 *   that's well inside USGS EPQS + Google Maps Elevation coverage, the
 *   user sees a silent `—m` chip with no explanation — they don't know
 *   whether to retry, redraw, contact support, or wait out a quota.
 *
 *   This helper turns the stage telemetry (the `bucket` strings emitted
 *   by `fetchPolygonElevations` and `fetchSinglePointElevation`) into a
 *   small enum of plausible root causes the UI can render as a chip.
 *
 * Inputs:
 *   - `stages`: ordered list of attempt outcomes (one per provider call).
 *     Each entry mirrors the structured telemetry shape used by
 *     `[provider:elevation] response` so the helper is the SAME data the
 *     existing Cloud Run logs already capture — no schema migration.
 *   - `polygon`: optional polygon vertices. Pre-classification geometry
 *     check (self-intersection, NaN, < 3 vertices) lets us short-circuit
 *     to `geometry-malformed` without waiting for a provider failure.
 *
 * Output:
 *   `{ classification, hint? }` — pure, no I/O, no React. The caller
 *   stamps this onto the SED slot's render path and the chip surfaces
 *   the classification description + a "Last attempted {ago}" suffix.
 *
 * Classifications (small, finite, ordered by precedence):
 *
 *   1. `geometry-malformed`  — polygon vertices are unusable
 *      (< 3 points, NaN/non-finite coord, |lat|>90, |lng|>180, or all
 *      vertices identical). Wins over any provider attempt — no point
 *      retrying with the same garbage geometry.
 *
 *   2. `provider-quota`      — at least one stage returned an HTTP 429
 *      or 403 (rate-limited / forbidden). Surfaces "Try again in a few
 *      minutes" hint.
 *
 *   3. `out-of-coverage`     — polygon centroid is offshore or outside
 *      the CONUS+AK+HI+Caribbean bounding box that USGS NED covers AND
 *      the Google fallback also returned `empty-results`. We treat
 *      empty-results on a centroid that's outside any USGS-covered land
 *      mass as a coverage gap rather than a transient failure.
 *
 *   4. `polygon-too-small`   — every stage returned `empty-results`
 *      AND the polygon's bounding box is below the empirical
 *      `MIN_USABLE_BBOX_DEG` threshold. Google's elevation grid is
 *      coarse; a polygon smaller than ~0.001° on a side can land
 *      entirely between sample points.
 *
 *   5. `unknown`             — falls through; chip still renders with
 *      the raw stage bucket list as `hint` so an operator can grep.
 *
 * Threshold choices:
 *   - `MIN_USABLE_BBOX_DEG = 0.0005°` (≈ 55 metres at the equator). The
 *     Davenport polygon spans ~0.01°, so it sits well above this floor —
 *     which is the point: Davenport WON'T classify as `polygon-too-small`,
 *     and the helper will fall through to `unknown` (or `out-of-coverage`
 *     if the centroid trips the bbox check — Davenport's centroid is
 *     ~(41.5, -90.5), inside CONUS, so it doesn't). Davenport's empirical
 *     classification is `unknown` until we add more provider hooks.
 *     That's deliberate — the helper doesn't lie about root cause when
 *     the telemetry doesn't pin it down.
 *
 * Pure of React / fetch — unit-testable with hand-rolled fixtures.
 */

/** Bucketed outcome of a single provider call. Mirrors the `bucket`
 *  values emitted by `[provider:elevation] response` telemetry across
 *  `fetchPolygonElevations.ts`, `refreshSiteElevation.ts`, and
 *  `services/dataProviders/siteProfile.ts`. */
export type ElevationStageBucket =
  | 'ok'
  | 'empty-results'
  | 'parse-error'
  | 'network-error'
  | 'too-few-vertices'
  | 'too-few-valid-samples'
  | 'invalid-coords'
  | 'non-200'
  | 'no-value';

/** One provider attempt — what the caller observed. */
export interface ElevationStage {
  /** Which subsystem produced the attempt (for chip "Last attempted ..."). */
  provider: 'google-polygon' | 'google-centroid' | 'usgs-epqs';
  /** Bucketed outcome (matches `[provider:elevation]` telemetry). */
  bucket: ElevationStageBucket;
  /** HTTP status when available — drives quota detection. `null` for
   *  pre-dispatch buckets (`too-few-vertices`, `invalid-coords`). */
  httpStatus?: number | null;
  /** Sample count returned (0 for every empty bucket). */
  pointsReturned?: number;
}

export type ElevationEmptyClassification =
  | 'geometry-malformed'
  | 'provider-quota'
  | 'out-of-coverage'
  | 'polygon-too-small'
  | 'unknown';

export interface ElevationEmptyDiagnosis {
  classification: ElevationEmptyClassification;
  /** Human-readable hint surfaced under the chip. */
  hint?: string;
}

/** Empirical floor — polygons below this bbox span on either axis
 *  routinely return `empty-results` from Google's coarse grid. Units:
 *  degrees. 0.0005° ≈ 55m at the equator. */
const MIN_USABLE_BBOX_DEG = 0.0005;

/** Conservative bounding box for the USGS NED elevation footprint
 *  (CONUS + Alaska + Hawaii + Caribbean territories). A centroid
 *  outside this box is "out of US elevation coverage" — the USGS EPQS
 *  provider can't help. */
const USGS_COVERAGE_BBOX = {
  // Includes Alaska, Hawaii, Puerto Rico / USVI.
  latMin: 15,
  latMax: 72,
  // -180 (Alaska west) through -64 (USVI east).
  lngMin: -180,
  lngMax: -64,
};

type LatLng = { lat: number; lng: number };

/** Coord validity — same predicate as `fetchPolygonElevations.ts`. */
function isCoordFinite(p: LatLng): boolean {
  return Number.isFinite(p.lat) && Number.isFinite(p.lng);
}

function isCoordInRange(p: LatLng): boolean {
  return Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180;
}

function isCentroidInUsgsCoverage(p: LatLng): boolean {
  return (
    p.lat >= USGS_COVERAGE_BBOX.latMin &&
    p.lat <= USGS_COVERAGE_BBOX.latMax &&
    p.lng >= USGS_COVERAGE_BBOX.lngMin &&
    p.lng <= USGS_COVERAGE_BBOX.lngMax
  );
}

/** Compute arithmetic centroid; safe for empty/short arrays
 *  (returns null when polygon is unusable). */
function centroidOf(polygon: LatLng[] | undefined): LatLng | null {
  if (!Array.isArray(polygon) || polygon.length === 0) return null;
  let latSum = 0;
  let lngSum = 0;
  for (const p of polygon) {
    if (!isCoordFinite(p)) return null;
    latSum += p.lat;
    lngSum += p.lng;
  }
  return { lat: latSum / polygon.length, lng: lngSum / polygon.length };
}

/** Polygon bbox span on each axis (degrees). Returns null if the
 *  polygon has < 1 vertex or any non-finite coord. */
function bboxSpan(polygon: LatLng[] | undefined): { lat: number; lng: number } | null {
  if (!Array.isArray(polygon) || polygon.length === 0) return null;
  let latMin = Infinity;
  let latMax = -Infinity;
  let lngMin = Infinity;
  let lngMax = -Infinity;
  for (const p of polygon) {
    if (!isCoordFinite(p)) return null;
    if (p.lat < latMin) latMin = p.lat;
    if (p.lat > latMax) latMax = p.lat;
    if (p.lng < lngMin) lngMin = p.lng;
    if (p.lng > lngMax) lngMax = p.lng;
  }
  return { lat: latMax - latMin, lng: lngMax - lngMin };
}

/** True when every vertex is identical (degenerate polygon — collapses
 *  to a point, no area). */
function allVerticesIdentical(polygon: LatLng[]): boolean {
  if (polygon.length < 2) return true;
  const first = polygon[0];
  for (let i = 1; i < polygon.length; i++) {
    if (polygon[i].lat !== first.lat || polygon[i].lng !== first.lng) return false;
  }
  return true;
}

/**
 * Classify the most plausible root cause of an "empty-samples" outcome.
 *
 * @param stages   Ordered list of provider attempts. Empty array =>
 *                 nothing tried (returns `unknown`).
 * @param polygon  Optional polygon vertices (used for geometry +
 *                 too-small detection). Pass `undefined` to skip
 *                 geometry checks (centroid-only refresh path).
 */
export function classifyElevationEmpty(
  stages: ElevationStage[],
  polygon?: LatLng[],
): ElevationEmptyDiagnosis {
  // 1. Geometry-malformed wins — no point retrying with garbage.
  //
  // Conditions: any vertex non-finite, |lat|>90, |lng|>180, fewer than 3
  // vertices when a polygon was supplied (less than a triangle), or all
  // vertices identical. Pre-dispatch buckets from the provider
  // (`too-few-vertices`, `invalid-coords`) also count.
  const preDispatchBuckets: ElevationStageBucket[] = [
    'too-few-vertices',
    'invalid-coords',
  ];
  const hasPreDispatchFailure = stages.some(s => preDispatchBuckets.includes(s.bucket));
  if (Array.isArray(polygon)) {
    if (polygon.length > 0 && polygon.length < 3) {
      return {
        classification: 'geometry-malformed',
        hint: `Polygon has only ${polygon.length} vertex(es); need at least 3.`,
      };
    }
    const anyNonFinite = polygon.some(p => !isCoordFinite(p));
    if (anyNonFinite) {
      return {
        classification: 'geometry-malformed',
        hint: 'One or more polygon vertices contained NaN/infinite coordinates.',
      };
    }
    const anyOutOfRange = polygon.some(p => isCoordFinite(p) && !isCoordInRange(p));
    if (anyOutOfRange) {
      return {
        classification: 'geometry-malformed',
        hint: 'One or more polygon vertices are outside the valid lat/lng range.',
      };
    }
    if (polygon.length >= 3 && allVerticesIdentical(polygon)) {
      return {
        classification: 'geometry-malformed',
        hint: 'All polygon vertices collapse to a single point.',
      };
    }
  }
  if (hasPreDispatchFailure) {
    return {
      classification: 'geometry-malformed',
      hint: 'Provider rejected the polygon before dispatch (too few vertices or invalid coords).',
    };
  }

  // 2. Provider quota — any stage hit 429 or 403. Surfaces a "retry
  //    later" hint without conflating with coverage.
  const quotaStage = stages.find(s => {
    if (!Number.isFinite(s.httpStatus)) return false;
    return s.httpStatus === 429 || s.httpStatus === 403;
  });
  if (quotaStage) {
    return {
      classification: 'provider-quota',
      hint: `${quotaStage.provider} returned HTTP ${quotaStage.httpStatus}. Try again in a few minutes.`,
    };
  }

  // 3. Out-of-coverage — centroid outside USGS NED bbox AND every
  //    attempt returned empty-results. Without geometry we can't tell;
  //    skip to (4)/(5).
  const allEmptyResults =
    stages.length > 0 &&
    stages.every(s => s.bucket === 'empty-results' || s.bucket === 'no-value');
  const centroid = centroidOf(polygon);
  if (allEmptyResults && centroid && !isCentroidInUsgsCoverage(centroid)) {
    return {
      classification: 'out-of-coverage',
      hint: 'Polygon centroid is outside the USGS NED footprint and Google returned no samples.',
    };
  }

  // 4. Polygon-too-small — bbox below the empirical floor on either
  //    axis. The polygon may be small enough to fall between Google's
  //    grid points (Google's elevation API samples a coarse DEM).
  const span = bboxSpan(polygon);
  if (allEmptyResults && span && (span.lat < MIN_USABLE_BBOX_DEG || span.lng < MIN_USABLE_BBOX_DEG)) {
    return {
      classification: 'polygon-too-small',
      hint: `Polygon bbox (${span.lat.toFixed(4)}° × ${span.lng.toFixed(4)}°) is below the ${MIN_USABLE_BBOX_DEG}° elevation-grid floor.`,
    };
  }

  // 5. Unknown — fall through. Surface the raw bucket list so an
  //    operator can grep.
  const bucketSummary = stages.map(s => `${s.provider}:${s.bucket}`).join(', ');
  return {
    classification: 'unknown',
    hint: stages.length > 0
      ? `No diagnostic match. Stages: ${bucketSummary}`
      : 'No provider attempts recorded.',
  };
}

/** Human-readable description of a classification — used as the chip
 *  label so the UI doesn't repeat the enum string verbatim. */
export function describeElevationEmpty(c: ElevationEmptyClassification): string {
  switch (c) {
    case 'geometry-malformed':
      return 'Polygon geometry is malformed';
    case 'provider-quota':
      return 'Provider quota exhausted';
    case 'out-of-coverage':
      return 'Outside USGS / Google coverage';
    case 'polygon-too-small':
      return 'Polygon too small for the elevation grid';
    case 'unknown':
      return 'No elevation data returned';
  }
}
